import {
  authenticatedApi,
  ApiError,
  API_BASE_URL
} from './apiUtils';

export const idpApi = {
  // Lookup Tables (Read-Only)
  getMeasurementTypes: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/idp/measurement-types`);
    } catch (error) {
      throw new ApiError('Failed to fetch measurement types', error.status || 500);
    }
  },

  getRAGStatuses: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/idp/rag-statuses`);
    } catch (error) {
      throw new ApiError('Failed to fetch RAG statuses', error.status || 500);
    }
  },

  getGoalStatuses: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/idp/goal-statuses`);
    } catch (error) {
      throw new ApiError('Failed to fetch goal statuses', error.status || 500);
    }
  },

  getGoalStatusTransitions: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/idp/goal-status-transitions`);
    } catch (error) {
      throw new ApiError('Failed to fetch goal status transitions', error.status || 500);
    }
  },

  getResourceTemplates: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/idp/resource-templates`);
    } catch (error) {
      throw new ApiError('Failed to fetch resource templates', error.status || 500);
    }
  },

  // My Development Goals (current user)
  getMyDevelopmentGoals: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.cursor) queryParams.append('cursor', params.cursor);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.orderBy) queryParams.append('orderBy', params.orderBy);
      if (params.orderDirection) queryParams.append('orderDirection', params.orderDirection);
      if (params.search) queryParams.append('search', params.search);
      if (params.statusFilter) queryParams.append('statusFilter', params.statusFilter);

      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/development-goals/me${queryString ? `?${queryString}` : ''}`;
      return await authenticatedApi.get(url);
    } catch (error) {
      throw new ApiError('Failed to fetch my development goals', error.status || 500);
    }
  },

  // Development Goals (all)
  getAllDevelopmentGoals: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.cursor) queryParams.append('cursor', params.cursor);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.orderBy) queryParams.append('orderBy', params.orderBy);
      if (params.orderDirection) queryParams.append('orderDirection', params.orderDirection);
      if (params.search) queryParams.append('search', params.search);
      if (params.statusFilter) queryParams.append('statusFilter', params.statusFilter);
      if (params.measurementTypeId) queryParams.append('measurementTypeId', params.measurementTypeId);
      if (params.employeeId) queryParams.append('employeeId', params.employeeId);

      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/development-goals${queryString ? `?${queryString}` : ''}`;
      return await authenticatedApi.get(url);
    } catch (error) {
      throw new ApiError('Failed to fetch development goals', error.status || 500);
    }
  },

  getDevelopmentGoalById: async (id) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/development-goals/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Development goal not found', 404);
      }
      throw new ApiError('Failed to fetch development goal', error.status || 500);
    }
  },

  saveDevelopmentGoal: async (goalData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/development-goals`, goalData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid goal data', 400);
      }
      if (error.status === 404) {
        throw new ApiError('Development goal not found', 404);
      }
      throw new ApiError('Failed to save development goal', error.status || 500);
    }
  },

  updateGoalStatus: async (id, statusId, reason) => {
    try {
      return await authenticatedApi.patch(`${API_BASE_URL}/development-goals/${id}/status`, {
        statusId,
        reason
      });
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Development goal not found', 404);
      }
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid status transition', 400);
      }
      throw new ApiError('Failed to update goal status', error.status || 500);
    }
  },

  deleteDevelopmentGoal: async (id) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/development-goals/${id}`);
    } catch (error) {
      if (error.status === 404) {
        throw new ApiError('Development goal not found', 404);
      }
      throw new ApiError(error.data?.error || 'Failed to delete development goal', error.status || 500);
    }
  },

  // Action Status Updates
  updateActionStatus: async (actionId, statusData) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/development-actions/${actionId}/status`, statusData);
    } catch (error) {
      if (error.status === 400) {
        throw new ApiError(error.data?.error || 'Invalid status data', 400);
      }
      throw new ApiError('Failed to update action status', error.status || 500);
    }
  },

  getActionStatusHistory: async (actionId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/development-actions/${actionId}/status-history`);
    } catch (error) {
      throw new ApiError('Failed to fetch action status history', error.status || 500);
    }
  },

  getGoalStatusHistory: async (goalId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/development-goals/${goalId}/status-history`);
    } catch (error) {
      throw new ApiError('Failed to fetch goal status history', error.status || 500);
    }
  },
};
